import { useEffect, useState } from "react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, KeyRound, ShieldCheck, ShieldOff, CopyIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
    { value: "office_admin", label: "Office Admin" },
    { value: "branch_manager", label: "Branch Manager" },
    { value: "marketing_executive", label: "Marketing Executive" },
    { value: "agent", label: "Agent" },
    { value: "telecaller", label: "Telecaller" },
    { value: "customer", label: "Customer" },
    { value: "ceo", label: "CEO" },
];

function genTempPassword() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789abcdefghjkmnpqrstuvwxyz@#$";
    let out = "K@";
    for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
}

export default function EmployeeManagement() {
    const [rows, setRows] = useState([]);
    const [branches, setBranches] = useState([]);
    const [open, setOpen] = useState(false);
    const [reset, setReset] = useState(null); // {employee, temp_password}
    const [created, setCreated] = useState(null); // {employee_id, temporary_password, email}
    const [form, setForm] = useState({
        name: "", email: "", phone: "", role: "agent", branch_id: "", department: "",
        password: genTempPassword(),
    });

    const load = async () => {
        const [u, b] = await Promise.all([api.get("/employees"), api.get("/branches")]);
        setRows(u.data);
        setBranches(b.data);
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/employees", form);
            setCreated({
                employee_id: data.employee_id, email: form.email,
                temporary_password: data.temporary_password,
            });
            setOpen(false);
            setForm({ name: "", email: "", phone: "", role: "agent", branch_id: "", department: "", password: genTempPassword() });
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/employees/${u.id}`, { active: !u.active });
            toast.success(u.active ? "Deactivated" : "Activated");
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const forceReset = async (u) => {
        try {
            const temp = genTempPassword();
            await api.post(`/employees/${u.id}/force-password-reset`, { temp_password: temp });
            setReset({ employee: u, temp_password: temp });
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

    return (
        <div className="space-y-6" data-testid="employees-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">People Ops</p>
                    <h1 className="font-serif text-3xl text-komma-navy">Employee Management</h1>
                    <p className="text-sm text-gray-600 mt-1">Only CEO/Office Admin can onboard employees. Never share passwords over insecure channels.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-komma-navy hover:bg-komma-navy-light" data-testid="add-employee-btn">
                            <UserPlus size={16} className="mr-2" /> Add Employee
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="font-serif text-komma-navy">Onboard Employee</DialogTitle></DialogHeader>
                        <form onSubmit={create} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Full name</Label>
                                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10" data-testid="emp-name" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Email</Label>
                                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-10" data-testid="emp-email" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Phone</Label>
                                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10" data-testid="emp-phone" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Department</Label>
                                    <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1.5 h-10" data-testid="emp-department" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Role</Label>
                                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="emp-role"><SelectValue /></SelectTrigger>
                                        <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-komma-navy">Branch</Label>
                                    <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="emp-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                                        <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-komma-navy">Temporary password</Label>
                                <div className="flex gap-2 mt-1.5">
                                    <Input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-10 font-mono" data-testid="emp-password" />
                                    <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: genTempPassword() })}>Regenerate</Button>
                                </div>
                                <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Employee will be forced to change on first login.</p>
                            </div>
                            <Button type="submit" className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="emp-submit">Create Employee</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {(created || reset) && (
                <div className="komma-card p-6 border-komma-gold/40 bg-komma-gold/5" data-testid="temp-password-card">
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">
                        {created ? "New employee created" : "Password reset"}
                    </p>
                    <p className="font-serif text-lg text-komma-navy mt-1">
                        {created ? `${created.email} · ID ${created.employee_id}` : `${reset.employee.email} · ID ${reset.employee.employee_id}`}
                    </p>
                    <div className="mt-3 flex items-center gap-3 p-3 rounded-md bg-white border border-komma-gold/40">
                        <span className="text-xs uppercase tracking-widest text-gray-500">Temp password</span>
                        <code className="font-mono text-komma-navy">{created ? created.temporary_password : reset.temp_password}</code>
                        <Button size="sm" variant="outline" onClick={() => copy(created ? created.temporary_password : reset.temp_password)}>
                            <CopyIcon size={12} className="mr-1" /> Copy
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setCreated(null); setReset(null); }}>Done</Button>
                    </div>
                    <p className="mt-3 text-xs text-gray-600">Share this with the employee over a secure channel. They must change it on first login.</p>
                </div>
            )}

            <div className="komma-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-komma-navy/5">
                        <tr>{["Employee ID", "Name", "Role", "Branch", "Status", "Last Login", "Password Changed", "Actions"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-komma-navy">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => (
                            <tr key={u.id} className="border-t border-gray-100" data-testid={`emp-row-${u.id}`}>
                                <td className="px-4 py-3 font-mono text-xs text-komma-navy">{u.employee_id || "—"}</td>
                                <td className="px-4 py-3">
                                    <p className="font-semibold text-komma-navy">{u.name}</p>
                                    <p className="text-xs text-gray-500">{u.email}</p>
                                </td>
                                <td className="px-4 py-3 capitalize">{(u.role || "").replace("_", " ")}</td>
                                <td className="px-4 py-3 text-xs">{branches.find((b) => b.id === u.branch_id)?.name || "—"}</td>
                                <td className="px-4 py-3">
                                    <Badge variant="outline" className={u.active === false ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                                        {u.active === false ? "Inactive" : "Active"}
                                    </Badge>
                                    {u.must_change_password && <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-700 border-amber-200">Must Reset</Badge>}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                                <td className="px-4 py-3 text-xs text-gray-500">{u.password_changed_at ? new Date(u.password_changed_at).toLocaleString() : "Never"}</td>
                                <td className="px-4 py-3 flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => forceReset(u)} data-testid={`force-reset-${u.id}`}
                                        className="h-8 text-xs border-komma-gold text-komma-navy hover:bg-komma-gold/10">
                                        <KeyRound size={12} className="mr-1" /> Reset
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}
                                        className={`h-8 text-xs ${u.active === false ? "border-emerald-500 text-emerald-700" : "border-red-500 text-red-700"}`}>
                                        {u.active === false ? <><ShieldCheck size={12} className="mr-1" /> Activate</> : <><ShieldOff size={12} className="mr-1" /> Deactivate</>}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
